"use client";

import { useEffect, useState, useRef, useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { setNavigationLoaderCallback } from "@/app/lib/navigationLoader";
import { useSidebarStore } from "@/app/stores/sidebarStore";
import Skeleton from "@/components/ui/skeleton";
import DashboardSkeleton from "@/app/dashboard/loading";
import ScheduleSkeleton from "@/app/dashboard/schedule/loading";
import StudyGoalSkeleton from "@/app/dashboard/studyGoal/loading";
import StudyNoteSkeleton from "@/app/dashboard/studyNote/loading";
import LearnerSkeleton from "@/app/dashboard/learner/loading";
import PromptSkeleton from "@/app/dashboard/prompt/loading";
import ReminderSkeleton from "@/app/dashboard/reminder/loading";
import SettingsSkeleton from "@/app/dashboard/settings/loading";
import ProgressTrackerSkeleton from "@/app/dashboard/progress-tracker/loading";
import SupportSkeleton from "@/app/dashboard/support/loading";
import AnalyticsSkeleton from "@/app/dashboard/analytics/loading";
import AudioInteractionSkeleton from "@/app/dashboard/audiointeraction/loading";
import QuizesSkeleton from "@/app/dashboard/quizes/loading";

const ROUTES_WITH_OWN_LOADERS = [
  "/dashboard/learner/session/",
];

type SkeletonMatcher = {
  test: (path: string) => boolean;
  render: () => JSX.Element;
};

const withShimmer = (content: JSX.Element, wrapperClass = "") => (
  <div className={`relative overflow-hidden ${wrapperClass}`}>
    <div className="absolute inset-0 shimmer-overlay" />
    <div className="relative">{content}</div>
  </div>
);

const isAuthRoute = (path?: string | null) => {
  if (!path) return false;
  const normalized = normalizePath(path);
  if (!normalized) return false;
  return normalized === "/auth/login" || normalized === "/auth/signup" || normalized.startsWith("/auth");
};

const routeSkeletons: SkeletonMatcher[] = [
  {
    test: (path) => path === "/dashboard",
    render: () => withShimmer(<DashboardSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/schedule"),
    render: () => withShimmer(<ScheduleSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/studyGoal"),
    render: () => withShimmer(<StudyGoalSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/studyNote"),
    render: () => withShimmer(<StudyNoteSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/learner"),
    render: () => withShimmer(<LearnerSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/prompt"),
    render: () => withShimmer(<PromptSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/reminder"),
    render: () => withShimmer(<ReminderSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/settings"),
    render: () => withShimmer(<SettingsSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/progress-tracker"),
    render: () => withShimmer(<ProgressTrackerSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/support"),
    render: () => withShimmer(<SupportSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/analytics"),
    render: () => withShimmer(<AnalyticsSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/audiointeraction"),
    render: () => withShimmer(<AudioInteractionSkeleton />),
  },
  {
    test: (path) => path.startsWith("/dashboard/quizes"),
    render: () => withShimmer(<QuizesSkeleton />),
  },
];

const defaultSkeleton = () => (
  withShimmer(
    <div className="p-6 w-full">
      <div className="flex flex-col gap-6 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-1/3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>
    </div>
  )
);

const DesktopNavbarSkeleton = () => (
  withShimmer(
    <div className="hidden md:flex items-center justify-between py-2 px-4 bg-white border-b border-[#E1E1E1]">
      <Skeleton className="h-6 w-48 rounded-full" />
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-48 rounded-full" />
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-10 w-10 rounded-full" />
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>
    </div>,
    "hidden md:flex"
  )
);

const MobileNavbarSkeleton = () => (
  withShimmer(
    <div className="md:hidden flex items-center justify-between py-2 px-4 bg-white border-b border-gray-200">
      <Skeleton className="h-9 w-9 rounded-lg" />
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-9 rounded-full" />
        <Skeleton className="h-9 w-9 rounded-full" />
        <Skeleton className="h-9 w-9 rounded-full" />
      </div>
    </div>,
    "md:hidden"
  )
);

const normalizePath = (path?: string | null) => {
  if (!path) return null;
  const [base] = path.split("?")[0].split("#");
  return base;
};

export default function NavigationLoader() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showLoader, setShowLoader] = useState(false);
  const pathnameRef = useRef(pathname);
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clickTargetRef = useRef<string | null>(null);
  const shouldShowLoaderRef = useRef(false);
  const { isOpen } = useSidebarStore();
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );

  const sidebarWidth = useMemo(() => {
    if (!isDesktop) return 0;
    return isOpen ? 256 : 80;
  }, [isDesktop, isOpen]);

  useEffect(() => {
    const updateViewportState = () => {
      const desktop = window.innerWidth >= 768;
      setIsDesktop(desktop);
    };

    updateViewportState();
    window.addEventListener("resize", updateViewportState);
    return () => window.removeEventListener("resize", updateViewportState);
  }, []);

  useEffect(() => {
    setNavigationLoaderCallback((show, path) => {
      if (show && path) {
        const normalizedPath = normalizePath(path);
        if (isAuthRoute(normalizedPath)) {
          shouldShowLoaderRef.current = false;
          setShowLoader(false);
          return;
        }
        const hasOwnLoader = ROUTES_WITH_OWN_LOADERS.some((route) =>
          normalizedPath?.includes(route)
        );
        if (!hasOwnLoader && normalizedPath !== normalizePath(pathname)) {
          clickTargetRef.current = normalizedPath;
          shouldShowLoaderRef.current = true;
          requestAnimationFrame(() => {
            setShowLoader(true);
          });
        }
      } else {
        setShowLoader(false);
        clickTargetRef.current = null;
      }
    });

    return () => {
      setNavigationLoaderCallback(() => {});
    };
  }, [pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href) return;

      const normalizedHref = normalizePath(href);
      if (!normalizedHref) return;

      if (isAuthRoute(normalizedHref)) return;

      const isInternalRoute =
        normalizedHref.startsWith("/dashboard") ||
        normalizedHref.startsWith("/auth") ||
        (normalizedHref.startsWith("/") &&
          !normalizedHref.startsWith("http"));

      if (!isInternalRoute) return;

      const hasOwnLoader = ROUTES_WITH_OWN_LOADERS.some((route) =>
        normalizedHref.includes(route)
      );

      if (hasOwnLoader) return;

      const currentPath = normalizePath(pathname);
      if (normalizedHref === currentPath) return;

      clickTargetRef.current = normalizedHref;
      shouldShowLoaderRef.current = true;
      requestAnimationFrame(() => {
        setShowLoader(true);
      });
    };

    const handleNavigationStart = (e: Event) => {
      const customEvent = e as CustomEvent;
      const path = normalizePath(customEvent.detail?.path);
      if (!path) return;

      if (isAuthRoute(path)) return;

      const hasOwnLoader = ROUTES_WITH_OWN_LOADERS.some((route) =>
        path.includes(route)
      );

      if (hasOwnLoader) return;

      if (path !== normalizePath(pathname)) {
        clickTargetRef.current = path;
        shouldShowLoaderRef.current = true;
        requestAnimationFrame(() => {
          setShowLoader(true);
        });
      }
    };

    document.addEventListener("click", handleClick, true);
    window.addEventListener("navigation-start", handleNavigationStart);

    return () => {
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("navigation-start", handleNavigationStart);
    };
  }, [pathname]);

  useEffect(() => {
    const hasOwnLoader = ROUTES_WITH_OWN_LOADERS.some((route) =>
      pathname?.includes(route)
    );

    if (isAuthRoute(pathname)) {
      shouldShowLoaderRef.current = false;
      setShowLoader(false);
      clickTargetRef.current = null;
      return;
    }

    if (hasOwnLoader) {
      shouldShowLoaderRef.current = false;
      setShowLoader(false);
      clickTargetRef.current = null;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      pathnameRef.current = pathname;
      return;
    }

    const pathnameChanged = pathnameRef.current !== pathname;
    pathnameRef.current = pathname;

    if (pathnameChanged) {
      if (!showLoader && !shouldShowLoaderRef.current) {
        shouldShowLoaderRef.current = true;
        setShowLoader(true);
      }
      if (shouldShowLoaderRef.current) {
        setShowLoader(true);
      }
      clickTargetRef.current = null;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!pathnameChanged && !showLoader) {
      return;
    }

    const checkForSkeletons = () => {
      const overlay = document.querySelector(
        "[data-navigation-overlay='true']"
      );
      const isInsideOverlay = (element: Element) =>
        overlay ? overlay.contains(element) : false;

      const skeletons = Array.from(
        document.querySelectorAll(
          '[class*="skeleton"], [class*="Skeleton"], [class*="animate-pulse"]'
        )
      ).filter((element) => !isInsideOverlay(element));

      const loadingIndicators = Array.from(
        document.querySelectorAll(
          '[data-loading], [aria-busy="true"], [class*="loading"]'
        )
      ).filter((element) => !isInsideOverlay(element));

      if (skeletons.length > 0 || loadingIndicators.length > 0) {
        shouldShowLoaderRef.current = false;
        setShowLoader(false);
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        return true;
      }
      return false;
    };

    observerRef.current = new MutationObserver(() => {
      checkForSkeletons();
    });

    setTimeout(() => {
      if (observerRef.current) {
        observerRef.current.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class", "data-loading", "aria-busy"],
        });

        if (checkForSkeletons()) {
          return;
        }
      }

      const checkInterval = setInterval(() => {
        if (checkForSkeletons()) {
          clearInterval(checkInterval);
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 2000);
    }, 50);

    timeoutRef.current = setTimeout(() => {
      shouldShowLoaderRef.current = false;
      setShowLoader(false);
      clickTargetRef.current = null;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    }, 2000);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [pathname, searchParams, showLoader]);

  if (
    ROUTES_WITH_OWN_LOADERS.some((route) => pathname?.includes(route)) ||
    !showLoader ||
    isAuthRoute(clickTargetRef.current) ||
    isAuthRoute(pathname)
  ) {
    return null;
  }

  const pendingPath = normalizePath(clickTargetRef.current) ??
    normalizePath(pathname);

  const SkeletonComponent =
    routeSkeletons.find((entry) => entry.test(pendingPath ?? ""))?.render() ??
    defaultSkeleton();

  return (
    <div
      data-navigation-overlay="true"
      className="fixed inset-0 z-[40] pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div
        className="absolute inset-y-0 right-0 flex flex-col bg-white/90 md:bg-[#F5F5F5] pointer-events-auto"
        style={{ left: isDesktop ? `${sidebarWidth}px` : "0px" }}
      >
        <DesktopNavbarSkeleton />
        <MobileNavbarSkeleton />
        <div className="flex-1 overflow-y-auto pb-10 md:pb-6">
          {SkeletonComponent}
        </div>
      </div>
    </div>
  );
}
